/**
 * Human-readable legend for an admin notification row.
 *
 * @param {{ kind?: string }|null|undefined} row
 * @returns {string}
 */
export function getNotificationLegend(row) {
    if (row?.kind === 'chargeback_batch_unlinked') {
        return 'Chargebacks could not be linked';
    }
    if (row?.kind === 'account_signup') {
        return 'New customer signed up';
    }
    if (row?.kind === 'account_first_transaction') {
        return 'First payment received';
    }
    return 'Mentioned you in a comment';
}

const ACCOUNT_EVENT_KINDS = new Set(['account_signup', 'account_first_transaction']);

/**
 * Who the notification is from; account events are system generated and have no actor.
 *
 * @param {{ kind?: string, actor?: { first_name?: string, last_name?: string }|null }|null|undefined} row
 * @returns {string}
 */
export function getNotificationActorLabel(row) {
    if (ACCOUNT_EVENT_KINDS.has(row?.kind)) {
        return 'Ecart Pay';
    }
    const actor = row?.actor;
    if (actor && (actor.first_name || actor.last_name)) {
        return `${actor.first_name || ''} ${actor.last_name || ''}`.trim();
    }
    return 'Teammate';
}

/**
 * Splits a notification preview into plain text and @mention segments.
 * Uses ordered mention labels (no @ prefix) with indexOf(token, searchStart) on the full string
 * so repeated names and multiple mentions resolve correctly.
 *
 * @param {string} preview
 * @param {string[]|undefined} mentionLabels
 * @returns {{ type: 'text' | 'mention', value: string }[]}
 */
export function getNotificationPreviewParts(preview, mentionLabels) {
    const raw = String(preview ?? '');
    const text = raw.normalize('NFC').trim();
    if (!text) {
        return [];
    }

    const labels = Array.isArray(mentionLabels)
        ? mentionLabels.map((l) => String(l || '').trim().normalize('NFC')).filter(Boolean)
        : [];

    if (labels.length === 0) {
        return [{ type: 'text', value: text }];
    }

    const parts = [];
    let searchStart = 0;

    for (const lb of labels) {
        const token = `@${lb}`;
        const idx = text.indexOf(token, searchStart);
        if (idx === -1) {
            return [{ type: 'text', value: text }];
        }
        if (idx > searchStart) {
            parts.push({ type: 'text', value: text.slice(searchStart, idx) });
        }
        parts.push({ type: 'mention', value: lb });
        searchStart = idx + token.length;
    }

    if (searchStart < text.length) {
        parts.push({ type: 'text', value: text.slice(searchStart) });
    }

    return parts.length > 0 ? parts : [{ type: 'text', value: text }];
}
